import {
  Component,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  Input,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  HostBinding,
  Optional,
  Self,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import {
  DateRangePickerOptionsInterface,
  DefaultDateRangePickerOptions,
} from '../@models/date-range-picker.options';
import { MwDateRangePickerComponent } from '../date-range-picker.component';
import { MwOverlayComponent, MwOverlayService } from '@core/components/overlay';
import { DateRangePickerInputEvent } from '../@models/date-range-picker-range.model';
import { MwDateRangePickerService } from '../@service/date-range-picker.service';
import { MwDateRangePickerLocaleService } from '../date-range-picker-locale.service';

@Component({
  selector: 'mw-date-range-picker',
  templateUrl: './date-range-picker-input.component.html',
  styleUrls: ['./date-range-picker-input.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: MwDateRangePickerInputComponent,
    },
  ],
})
export class MwDateRangePickerInputComponent
  implements
    AfterViewInit,
    OnDestroy,
    MatFormFieldControl<DateRangePickerInputEvent>,
    ControlValueAccessor
{
  static nextId = 0;

  localOptions: DateRangePickerOptionsInterface;

  stateChanges = new Subject<void>();
  controlType = 'mw-date-picker';
  autofilled = false;

  @HostBinding()
  id = `mw-date-range-picker-${MwDateRangePickerInputComponent.nextId++}`;

  @HostBinding('attr.aria-describedby') describedBy = '';

  @ViewChild('trigger', { static: true }) containerRef: any;
  @ViewChild('inputRef', { static: true }) inputRef: any;

  @Input()
  get options(): DateRangePickerOptionsInterface {
    return this.localOptions;
  }
  set options(opts: DateRangePickerOptionsInterface) {
    this.localOptions = { ...DefaultDateRangePickerOptions, ...opts };
    this.localOptions.locale = {
      ...this.localOptions.locale,
      ...this.localeService.config,
    };

    this.service.setup(this.localOptions);
    this.setInputText();
  }
  @Input()
  get value(): DateRangePickerInputEvent {
    return this.localValue;
  }
  set value(value: DateRangePickerInputEvent) {
    this.localValue = value;

    if (!this.options) {
      this.options = null;
    }

    this.setInputText();
  }
  @Input()
  get placeholder(): string {
    return this.localPlaceholder;
  }
  set placeholder(value) {
    this.localPlaceholder = value;
    this.stateChanges.next();
  }
  @Input()
  get disabled(): boolean {
    return this.localDisabled;
  }
  set disabled(value: boolean) {
    this.localDisabled = value;
    this.stateChanges.next();
  }
  @Input()
  get required(): boolean {
    return this.localRequired;
  }
  set required(value) {
    this.localRequired = value;
    this.stateChanges.next();
  }

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() open = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close = new EventEmitter();
  @Output() rangeChange = new EventEmitter<DateRangePickerInputEvent>();

  get focused(): boolean {
    return (this.overlay && this.overlay.isOpen) || this.inputFieldFocused;
  }

  get empty(): boolean {
    return this.value == null;
  }

  get errorState(): boolean {
    if (this.ngControl) {
      return this.ngControl.invalid && this.ngControl.touched;
    } else {
      return false;
    }
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  private overlay: MwOverlayComponent<MwDateRangePickerComponent>;
  private dateRangePickerComponent: MwDateRangePickerComponent;

  private localValue: DateRangePickerInputEvent;
  private localRequired: boolean;
  private localDisabled: boolean;
  private localPlaceholder = this.translateService.instant(
    'CORE.COMPONENT.DATE_RANGE_PICKER.select_date_range'
  );
  private onChangeCallback: (dateRange: DateRangePickerInputEvent) => void;
  private onTouchedCallback: () => void;
  private inputFieldFocused = false;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private changeDetectorRef: ChangeDetectorRef,
    private overlayService: MwOverlayService,
    private translateService: TranslateService,
    private service: MwDateRangePickerService,
    private localeService: MwDateRangePickerLocaleService
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    this.overlay = this.overlayService.create(
      MwDateRangePickerComponent,
      this.containerRef
    );

    this.setInputText();

    this.overlay.opened.subscribe((instance) => {
      this.dateRangePickerComponent = instance;
      this.dateRangePickerComponent.options = this.options;
      this.dateRangePickerComponent.rangeChange.subscribe(
        (t: DateRangePickerInputEvent) => {
          this.value = t;
          if (this.onChangeCallback) {
            this.onChangeCallback(t);
          }
          this.rangeChange.emit(t);
        }
      );
      this.dateRangePickerComponent.closed.subscribe(() => {
        this.overlay.close();
      });
      this.dateRangePickerComponent.value = this.value;

      this.open.emit();
    });

    this.overlay.closed.subscribe(() => {
      this.dateRangePickerComponent = null;
      this.close.emit();
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.overlay.dispose();
  }

  onClear(e): void {
    this.value = null;
    if (this.onChangeCallback) {
      this.onChangeCallback(null);
    }
    this.rangeChange.emit(null);
    e.stopPropagation();
  }

  onInputBlur(event): void {
    const targetClasses: string =
      event.relatedTarget &&
      event.relatedTarget.parentElement &&
      event.relatedTarget.parentElement.className;
    const lostFocusToSelectField = !targetClasses;
    if (lostFocusToSelectField) {
      if (this.onTouchedCallback) {
        this.onTouchedCallback();
      }
    }
    this.inputFieldFocused = false;
    this.stateChanges.next();
  }

  onTriggerClick(): void {
    this.openDateRangePicker();
  }

  // For MatFormFieldControl
  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    this.inputRef.nativeElement.focus();
  }

  // For ControlValueAccessor
  writeValue(value: any): void {
    this.value = value;
  }
  registerOnChange(callback: () => void): void {
    this.onChangeCallback = callback;
  }
  registerOnTouched(callback: () => void): void {
    this.onTouchedCallback = callback;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  toggleCalendar(): void {
    if (this.dateRangePickerComponent) {
      this.closeDateRangePicker();
    } else {
      this.openDateRangePicker();
    }
  }

  openDateRangePicker(): void {
    this.overlay.show();
  }

  closeDateRangePicker(): void {
    this.overlay.close();
  }

  private setInputText(): void {
    let text = '';

    if (this.value) {
      const isShowAsDate = this.options && this.options.isShowAsDate;

      text = this.service.getRangeText(
        this.localValue.label,
        this.localValue.start,
        this.localValue.end,
        isShowAsDate
      );
    }

    this.inputRef.nativeElement.value = text;
  }
}
